import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  EmailNotificationPreviewData,
  EmailNotificationSettings,
  EmailNotificationUsage,
  getEmailNotificationCreatedPreview,
  getEmailNotificationDepositPendingPreview,
  getEmailNotificationSettings,
  isEmailUsageNearLimit,
  REMINDER_HOUR_PRESETS,
  updateEmailNotificationSettings,
} from "../../../services/emailNotificationSettingsService";
import {
  DEFAULT_TENANT_SETTINGS,
  getSettings,
  updateSettings,
} from "../../../services/settingsService";
import { getMessagingOutboundPreview } from "../../../services/messagingSettingsService";

const DEFAULT_SETTINGS: EmailNotificationSettings = {
  client_email_notifications_enabled: true,
  appointment_created_notifications_enabled: true,
  appointment_reminder_notifications_enabled: true,
  appointment_reminder_hours_before: 24,
  notify_on_created_default: true,
  notify_reminder_default: true,
  client_email_reply_to: null,
};

const DEFAULT_USAGE: EmailNotificationUsage = {
  transactional_count: 0,
  campaign_count: 0,
  limit: null,
  is_free: false,
  is_near_limit: false,
  is_at_limit: false,
};

const EmailNotificationsSettingsPage = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [settings, setSettings] = useState<EmailNotificationSettings>(DEFAULT_SETTINGS);
  const [usage, setUsage] = useState<EmailNotificationUsage>(DEFAULT_USAGE);
  const [initialSettings, setInitialSettings] = useState<EmailNotificationSettings>(DEFAULT_SETTINGS);
  const [preview, setPreview] = useState<EmailNotificationPreviewData | null>(null);
  const [depositPreview, setDepositPreview] = useState<EmailNotificationPreviewData | null>(null);
  const [loadingDepositPreview, setLoadingDepositPreview] = useState(false);
  const [reviewEnabled, setReviewEnabled] = useState(
    DEFAULT_TENANT_SETTINGS.appointment_reviews_enabled
  );
  const [reviewDelayHours, setReviewDelayHours] = useState(
    DEFAULT_TENANT_SETTINGS.appointment_review_delay_hours
  );
  const [initialReviewEnabled, setInitialReviewEnabled] = useState(
    DEFAULT_TENANT_SETTINGS.appointment_reviews_enabled
  );
  const [initialReviewDelayHours, setInitialReviewDelayHours] = useState(
    DEFAULT_TENANT_SETTINGS.appointment_review_delay_hours
  );
  const [savingReviews, setSavingReviews] = useState(false);
  const [reviewWhatsAppPreview, setReviewWhatsAppPreview] = useState<string | null>(null);
  const [loadingReviewWhatsAppPreview, setLoadingReviewWhatsAppPreview] = useState(false);

  useEffect(() => {
    document.title = t("settings.emailNotifications.pageTitle");
  }, [t]);

  const loadReviewWhatsAppPreview = useCallback(async () => {
    setLoadingReviewWhatsAppPreview(true);

    try {
      const response = await getMessagingOutboundPreview("review_request");
      setReviewWhatsAppPreview(response.data?.body ?? null);
    } catch {
      setReviewWhatsAppPreview(null);
    } finally {
      setLoadingReviewWhatsAppPreview(false);
    }
  }, []);

  useEffect(() => {
    void loadReviewWhatsAppPreview();
  }, [loadReviewWhatsAppPreview]);

  const applyResponse = useCallback((data: { settings: EmailNotificationSettings; usage: EmailNotificationUsage }) => {
    setSettings(data.settings);
    setUsage(data.usage);
    setInitialSettings(data.settings);
  }, []);

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);

    try {
      const response = await getEmailNotificationCreatedPreview();
      setPreview(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("settings.emailNotifications.preview.loadError"));
    } finally {
      setLoadingPreview(false);
    }
  }, [t]);

  const loadDepositPreview = useCallback(async () => {
    setLoadingDepositPreview(true);

    try {
      const response = await getEmailNotificationDepositPendingPreview();
      setDepositPreview(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("settings.emailNotifications.depositPreview.loadError"));
    } finally {
      setLoadingDepositPreview(false);
    }
  }, [t]);

  const loadSettings = useCallback(async () => {
    setLoading(true);

    try {
      const [response, tenantSettingsResponse] = await Promise.all([
        getEmailNotificationSettings(),
        getSettings(),
      ]);
      applyResponse(response.data);

      const tenantSettings = tenantSettingsResponse.data;
      if (tenantSettings) {
        setReviewEnabled(Boolean(tenantSettings.appointment_reviews_enabled ?? true));
        setReviewDelayHours(Number(tenantSettings.appointment_review_delay_hours ?? 2));
        setInitialReviewEnabled(Boolean(tenantSettings.appointment_reviews_enabled ?? true));
        setInitialReviewDelayHours(Number(tenantSettings.appointment_review_delay_hours ?? 2));
      }

      await Promise.all([loadPreview(), loadDepositPreview()]);
    } catch (error: any) {
      toast.error(error?.data || t("settings.emailNotifications.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [applyResponse, loadDepositPreview, loadPreview, t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const isDirty = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(initialSettings),
    [initialSettings, settings]
  );

  const isReviewDirty = useMemo(
    () =>
      reviewEnabled !== initialReviewEnabled ||
      reviewDelayHours !== initialReviewDelayHours,
    [initialReviewDelayHours, initialReviewEnabled, reviewDelayHours, reviewEnabled]
  );

  const handleSave = async () => {
    setSaving(true);

    try {
      const response = await updateEmailNotificationSettings(settings);
      applyResponse(response.data);
      toast.success(response.message || t("settings.emailNotifications.toast.saveSuccess"));
      await loadPreview();
    } catch (error: any) {
      toast.error(error?.data || t("settings.emailNotifications.toast.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveReviews = async () => {
    setSavingReviews(true);

    try {
      const response = await updateSettings({
        appointment_reviews_enabled: reviewEnabled,
        appointment_review_delay_hours: reviewDelayHours,
      });
      const data = response.data;
      setReviewEnabled(Boolean(data?.appointment_reviews_enabled ?? reviewEnabled));
      setReviewDelayHours(Number(data?.appointment_review_delay_hours ?? reviewDelayHours));
      setInitialReviewEnabled(Boolean(data?.appointment_reviews_enabled ?? reviewEnabled));
      setInitialReviewDelayHours(Number(data?.appointment_review_delay_hours ?? reviewDelayHours));
      toast.success(response.message || t("settings.emailNotifications.reviews.toast.saveSuccess"));
    } catch (error: any) {
      toast.error(error?.data || t("settings.emailNotifications.reviews.toast.saveError"));
    } finally {
      setSavingReviews(false);
    }
  };

  const usageLabel = usage.is_free && usage.limit !== null
    ? t("settings.emailNotifications.usage.free", {
        count: usage.transactional_count,
        limit: usage.limit,
      })
    : t("settings.emailNotifications.usage.paid", {
        count: usage.transactional_count,
      });

  const renderUsageAlert = () => {
    if (!usage.is_free || usage.limit === null) {
      return (
        <p className="text-muted small mb-0">
          {t("settings.emailNotifications.usage.paidHint")}
        </p>
      );
    }

    if (usage.is_at_limit) {
      return (
        <Alert color="danger" className="mb-0">
          {t("settings.emailNotifications.usage.atLimit", {
            count: usage.transactional_count,
            limit: usage.limit,
          })}{" "}
          <Link to="/settings/subscription" className="alert-link">
            {t("settings.emailNotifications.usage.upgradeLink")}
          </Link>
        </Alert>
      );
    }

    if (usage.is_near_limit) {
      return (
        <Alert color="warning" className="mb-0">
          {t("settings.emailNotifications.usage.nearLimit", {
            count: usage.transactional_count,
            limit: usage.limit,
          })}{" "}
          <Link to="/settings/subscription" className="alert-link">
            {t("settings.emailNotifications.usage.upgradeLink")}
          </Link>
        </Alert>
      );
    }

    return (
      <Alert color="warning" className="mb-0">
        {t("settings.emailNotifications.usage.freeHint")}{" "}
        <Link to="/settings/subscription" className="alert-link">
          {t("settings.emailNotifications.usage.upgradeLink")}
        </Link>
      </Alert>
    );
  };

  return (
    <div className="page-content">
      <ToastContainer position="top-right" autoClose={3000} />
      <Container fluid>
        <BreadCrumb
          title={t("settings.emailNotifications.breadcrumb.title")}
          pageTitle={t("settings.emailNotifications.breadcrumb.pageTitle")}
        />

        <p className="text-muted mb-4">{t("settings.emailNotifications.pageHelper")}</p>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <Row>
            <Col lg={8}>
              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-0">{t("settings.emailNotifications.usage.title")}</h5>
                </CardHeader>
                <CardBody>
                  <p className="mb-2">{usageLabel}</p>
                  {renderUsageAlert()}
                </CardBody>
              </Card>

              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-0">{t("settings.emailNotifications.replyTo.title")}</h5>
                </CardHeader>
                <CardBody>
                  <p className="text-muted small mb-3">{t("settings.emailNotifications.replyTo.help")}</p>
                  <Label htmlFor="client-email-reply-to">
                    {t("settings.emailNotifications.replyTo.label")}
                  </Label>
                  <Input
                    id="client-email-reply-to"
                    type="email"
                    value={settings.client_email_reply_to ?? ""}
                    placeholder={t("settings.emailNotifications.replyTo.placeholder")}
                    onChange={(event) =>
                      setSettings((current) => ({
                        ...current,
                        client_email_reply_to: event.target.value.trim() === "" ? null : event.target.value.trim(),
                      }))
                    }
                  />
                </CardBody>
              </Card>

              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-0">{t("settings.emailNotifications.typeA.title")}</h5>
                </CardHeader>
                <CardBody>
                  <p className="text-muted small mb-4">{t("settings.emailNotifications.typeA.help")}</p>
                  <div className="form-check form-switch mb-3">
                    <Input
                      id="email-created-enabled"
                      type="checkbox"
                      role="switch"
                      className="form-check-input"
                      checked={settings.appointment_created_notifications_enabled}
                      onChange={(event) =>
                        setSettings((current) => ({
                          ...current,
                          appointment_created_notifications_enabled: event.target.checked,
                        }))
                      }
                    />
                    <Label className="form-check-label" htmlFor="email-created-enabled">
                      {t("settings.emailNotifications.typeA.toggle")}
                    </Label>
                  </div>
                  <div className="form-check form-switch mb-4">
                    <Input
                      id="notify-created-default"
                      type="checkbox"
                      role="switch"
                      className="form-check-input"
                      checked={settings.notify_on_created_default}
                      disabled={!settings.appointment_created_notifications_enabled}
                      onChange={(event) =>
                        setSettings((current) => ({
                          ...current,
                          notify_on_created_default: event.target.checked,
                        }))
                      }
                    />
                    <Label className="form-check-label" htmlFor="notify-created-default">
                      {t("settings.emailNotifications.typeA.default")}
                    </Label>
                  </div>

                  <div className="border-top pt-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <h6 className="mb-0">{t("settings.emailNotifications.preview.title")}</h6>
                      <Button color="light" size="sm" onClick={loadPreview} disabled={loadingPreview}>
                        {loadingPreview
                          ? t("settings.emailNotifications.preview.loading")
                          : t("settings.emailNotifications.preview.refresh")}
                      </Button>
                    </div>
                    <p className="text-muted small mb-3">{t("settings.emailNotifications.preview.help")}</p>
                    {loadingPreview && !preview ? (
                      <div className="text-center py-4">
                        <Spinner size="sm" color="primary" />
                      </div>
                    ) : preview ? (
                      <>
                        <p className="small text-muted mb-2">
                          <strong>{t("settings.emailNotifications.preview.subjectLabel")}:</strong>{" "}
                          {preview.subject}
                        </p>
                        <div
                          className="border rounded overflow-hidden bg-light"
                          style={{ maxHeight: 420, overflowY: "auto" }}
                        >
                          <iframe
                            title={t("settings.emailNotifications.preview.title")}
                            srcDoc={preview.html}
                            sandbox=""
                            style={{ width: "100%", minHeight: 360, border: 0, background: "#f4f2ef" }}
                          />
                        </div>
                      </>
                    ) : null}
                  </div>
                </CardBody>
              </Card>

              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-0">{t("settings.emailNotifications.typeB.title")}</h5>
                </CardHeader>
                <CardBody>
                  <p className="text-muted small mb-4">{t("settings.emailNotifications.typeB.help")}</p>
                  <div className="form-check form-switch mb-3">
                    <Input
                      id="email-reminder-enabled"
                      type="checkbox"
                      role="switch"
                      className="form-check-input"
                      checked={settings.appointment_reminder_notifications_enabled}
                      onChange={(event) =>
                        setSettings((current) => ({
                          ...current,
                          appointment_reminder_notifications_enabled: event.target.checked,
                        }))
                      }
                    />
                    <Label className="form-check-label" htmlFor="email-reminder-enabled">
                      {t("settings.emailNotifications.typeB.toggle")}
                    </Label>
                  </div>
                  <div className="mb-3">
                    <Label htmlFor="reminder-hours-before">
                      {t("settings.emailNotifications.typeB.hoursLabel")}
                    </Label>
                    <Input
                      id="reminder-hours-before"
                      type="select"
                      value={String(settings.appointment_reminder_hours_before)}
                      disabled={!settings.appointment_reminder_notifications_enabled}
                      onChange={(event) =>
                        setSettings((current) => ({
                          ...current,
                          appointment_reminder_hours_before: Number(event.target.value),
                        }))
                      }
                    >
                      {REMINDER_HOUR_PRESETS.map((hours) => (
                        <option key={hours} value={hours}>
                          {t("settings.emailNotifications.typeB.hoursOption", { count: hours })}
                        </option>
                      ))}
                    </Input>
                  </div>
                  <div className="form-check form-switch">
                    <Input
                      id="notify-reminder-default"
                      type="checkbox"
                      role="switch"
                      className="form-check-input"
                      checked={settings.notify_reminder_default}
                      disabled={!settings.appointment_reminder_notifications_enabled}
                      onChange={(event) =>
                        setSettings((current) => ({
                          ...current,
                          notify_reminder_default: event.target.checked,
                        }))
                      }
                    />
                    <Label className="form-check-label" htmlFor="notify-reminder-default">
                      {t("settings.emailNotifications.typeB.default")}
                    </Label>
                  </div>
                </CardBody>
              </Card>

              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-0">{t("settings.emailNotifications.depositPreview.title")}</h5>
                </CardHeader>
                <CardBody>
                  <p className="text-muted small mb-3">{t("settings.emailNotifications.depositPreview.help")}</p>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="mb-0">{t("settings.emailNotifications.depositPreview.sampleLabel")}</h6>
                    <Button
                      color="light"
                      size="sm"
                      onClick={loadDepositPreview}
                      disabled={loadingDepositPreview}
                    >
                      {loadingDepositPreview
                        ? t("settings.emailNotifications.preview.loading")
                        : t("settings.emailNotifications.preview.refresh")}
                    </Button>
                  </div>
                  {loadingDepositPreview && !depositPreview ? (
                    <div className="text-center py-4">
                      <Spinner size="sm" color="primary" />
                    </div>
                  ) : depositPreview ? (
                    <>
                      <p className="small text-muted mb-2">
                        <strong>{t("settings.emailNotifications.preview.subjectLabel")}:</strong>{" "}
                        {depositPreview.subject}
                      </p>
                      <div
                        className="border rounded overflow-hidden bg-light"
                        style={{ maxHeight: 420, overflowY: "auto" }}
                      >
                        <iframe
                          title={t("settings.emailNotifications.depositPreview.title")}
                          srcDoc={depositPreview.html}
                          sandbox=""
                          style={{ width: "100%", minHeight: 360, border: 0, background: "#f4f2ef" }}
                        />
                      </div>
                    </>
                  ) : null}
                </CardBody>
              </Card>

              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-0">{t("settings.emailNotifications.reviews.title")}</h5>
                </CardHeader>
                <CardBody>
                  <p className="text-muted small mb-4">{t("settings.emailNotifications.reviews.help")}</p>
                  <div className="form-check form-switch mb-3">
                    <Input
                      id="appointment-reviews-enabled"
                      type="checkbox"
                      role="switch"
                      className="form-check-input"
                      checked={reviewEnabled}
                      onChange={(event) => setReviewEnabled(event.target.checked)}
                    />
                    <Label className="form-check-label" htmlFor="appointment-reviews-enabled">
                      {t("settings.emailNotifications.reviews.toggle")}
                    </Label>
                  </div>
                  <Label htmlFor="appointment-review-delay-hours">
                    {t("settings.emailNotifications.reviews.delayLabel")}
                  </Label>
                  <Input
                    id="appointment-review-delay-hours"
                    type="number"
                    min={1}
                    max={72}
                    value={reviewDelayHours}
                    disabled={!reviewEnabled}
                    onChange={(event) =>
                      setReviewDelayHours(
                        Math.min(72, Math.max(1, Number(event.target.value) || 2))
                      )
                    }
                    className="mb-2"
                    style={{ maxWidth: 160 }}
                  />
                  <p className="text-muted small mb-3">
                    {t("settings.emailNotifications.reviews.delayHelp")}
                  </p>
                  <div className="border rounded p-3 bg-light mb-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <p className="mb-0 small fw-semibold">
                        {t("settings.emailNotifications.reviews.whatsAppPreviewTitle")}
                      </p>
                      <Button
                        color="link"
                        className="p-0"
                        size="sm"
                        onClick={() => void loadReviewWhatsAppPreview()}
                        disabled={loadingReviewWhatsAppPreview}
                      >
                        {loadingReviewWhatsAppPreview
                          ? t("settings.emailNotifications.preview.loading")
                          : t("settings.emailNotifications.preview.refresh")}
                      </Button>
                    </div>
                    <p className="text-muted small mb-2">
                      {t("settings.emailNotifications.reviews.whatsAppPreviewHelp")}
                    </p>
                    {loadingReviewWhatsAppPreview && !reviewWhatsAppPreview ? (
                      <Spinner size="sm" color="primary" />
                    ) : reviewWhatsAppPreview ? (
                      <p className="small mb-0" style={{ whiteSpace: "pre-wrap" }}>
                        {reviewWhatsAppPreview}
                      </p>
                    ) : (
                      <p className="text-muted small mb-0">
                        {t("settings.emailNotifications.reviews.whatsAppPreviewEmpty")}
                      </p>
                    )}
                  </div>
                  <Button
                    color="primary"
                    outline
                    size="sm"
                    onClick={() => void handleSaveReviews()}
                    disabled={!isReviewDirty || savingReviews}
                  >
                    {savingReviews
                      ? t("settings.emailNotifications.actions.saving")
                      : t("settings.emailNotifications.reviews.save")}
                  </Button>
                </CardBody>
              </Card>

              <div className="d-flex justify-content-end gap-2">
                <Button color="light" onClick={loadSettings} disabled={saving}>
                  {t("settings.emailNotifications.actions.reload")}
                </Button>
                <Button color="primary" onClick={handleSave} disabled={!isDirty || saving}>
                  {saving
                    ? t("settings.emailNotifications.actions.saving")
                    : t("settings.emailNotifications.actions.save")}
                </Button>
              </div>
            </Col>
            <Col lg={4}>
              <Card>
                <CardBody>
                  <Badge color={settings.client_email_notifications_enabled ? "success" : "secondary"}>
                    {settings.client_email_notifications_enabled
                      ? t("settings.emailNotifications.status.enabled")
                      : t("settings.emailNotifications.status.disabled")}
                  </Badge>
                  {isEmailUsageNearLimit(usage) ? (
                    <Alert color="warning" className="mt-3 mb-0">
                      {usage.is_at_limit
                        ? t("settings.emailNotifications.usage.sidebarAtLimit")
                        : t("settings.emailNotifications.usage.sidebarNearLimit")}{" "}
                      <Link to="/settings/subscription" className="alert-link">
                        {t("settings.emailNotifications.usage.upgradeLink")}
                      </Link>
                    </Alert>
                  ) : (
                    <p className="text-muted small mt-3 mb-0">
                      {t("settings.emailNotifications.sidebarHint")}
                    </p>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default EmailNotificationsSettingsPage;
